import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getGuideConfig } from '@/data/categoryGuides';
import type { GuideConfig } from '@/data/types';
import GuideAppHeader from '@/components/guide/GuideAppHeader';
import GuideStart from '@/components/guide/GuideStart';
import GuideProgress from '@/components/guide/GuideProgress';
import GuideQuestion from '@/components/guide/GuideQuestion';

type Step = 'start' | 'question' | 'analyzing';

const AREA_QUERY_KEY = 'areaName';
const ANALYZING_CHECK_ITEMS = [
  '선택 기준 분석 중',
  '선호도 패턴 확인 중',
  '스타일 매칭 중',
  '최적 결과 도출 중',
] as const;
const AREA_QUESTION_KEYWORDS = ['지역', '거주지', '예식'];
const AREA_OPTION_LABEL_MAP: Record<string, string> = {
  '서울 강남권': '서울',
  '서울 그 외': '서울',
  '경기 · 인천': '경기 · 인천',
  '그 외 지방': '기타',
};

function getAreaNameFromAnswers(config: GuideConfig, answers: Record<string, string[]>) {
  const areaQuestion = config.questions.find((question) => {
    const source = `${question.category || ''} ${question.questionText} ${question.helperText || ''}`;
    return AREA_QUESTION_KEYWORDS.some((keyword) => source.includes(keyword));
  });

  if (!areaQuestion) return '';

  const selectedId = answers[areaQuestion.id]?.[0];
  if (!selectedId) return '';

  const selectedOption = areaQuestion.options.find((option) => option.id === selectedId);
  if (!selectedOption) return '';

  return AREA_OPTION_LABEL_MAP[selectedOption.label] || selectedOption.label;
}

function buildResultPath(categoryKey: string, resultKey: string, areaName: string) {
  const searchParams = new URLSearchParams();

  if (areaName && areaName !== '전체') {
    searchParams.set(AREA_QUERY_KEY, areaName);
  }

  const queryString = searchParams.toString();
  return `/guide/${categoryKey}/result/${resultKey}${queryString ? `?${queryString}` : ''}`;
}

export default function GuideCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('start');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [analyzingChecks, setAnalyzingChecks] = useState<number>(0);
  const [hasSelection, setHasSelection] = useState(false);

  const config = category ? getGuideConfig(category) : null;
  const storageKey = config ? `guide:${config.categoryKey}` : '';

  useEffect(() => {
    if (!category || !config) {
      navigate('/guide', { replace: true });
    }
  }, [category, config, navigate]);

  const handleStart = useCallback(() => {
    setStep('question');
    setCurrentQuestionIndex(0);
    setAnswers({});
    setHasSelection(false);
  }, []);

  const handleHeaderBack = useCallback(() => {
    if (!config || step !== 'question') return;

    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      const prevQuestion = config.questions[prevIndex];
      const prevAnswer = answers[prevQuestion.id];
      setHasSelection(Boolean(prevAnswer && prevAnswer.length > 0));
      return;
    }

    setStep('start');
  }, [step, currentQuestionIndex, answers, config]);

  const handleAnswer = useCallback(
    (selectedIds: string[]) => {
      if (!config) return;

      const question = config.questions[currentQuestionIndex];
      setAnswers((prev) => ({ ...prev, [question.id]: selectedIds }));
      setHasSelection(selectedIds.length > 0);
    },
    [config, currentQuestionIndex]
  );

  const handleNext = useCallback(() => {
    if (!config) return;

    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= config.questions.length) {
      setStep('analyzing');
      setAnalyzingChecks(0);
      return;
    }

    setCurrentQuestionIndex(nextIndex);
    const nextQuestion = config.questions[nextIndex];
    const nextAnswer = answers[nextQuestion.id];
    setHasSelection(Boolean(nextAnswer && nextAnswer.length > 0));
  }, [currentQuestionIndex, config, answers]);

  useEffect(() => {
    if (!config || step !== 'analyzing') return;

    let cancelled = false;
    let currentCheck = 0;

    const showNext = () => {
      if (cancelled) return;

      if (currentCheck < ANALYZING_CHECK_ITEMS.length) {
        setAnalyzingChecks(currentCheck + 1);
        currentCheck += 1;
        setTimeout(showNext, 350);
        return;
      }

      setTimeout(() => {
        if (cancelled) return;

        const resultKey = config.calculateResult(answers);
        const areaName = getAreaNameFromAnswers(config, answers);

        try {
          localStorage.setItem(`${storageKey}:answers`, JSON.stringify(answers));
          localStorage.setItem(
            `${storageKey}:result`,
            JSON.stringify({ resultKey, areaName, completedAt: Date.now() })
          );
        } catch {
          // ignore
        }

        navigate(buildResultPath(config.categoryKey, resultKey, areaName), { replace: true });
      }, 500);
    };

    showNext();

    return () => {
      cancelled = true;
    };
  }, [step, config, answers, navigate, storageKey]);

  if (!config) {
    return null;
  }

  const currentQuestion = config.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex >= config.questions.length - 1;
  const savedSelectedIds = currentQuestion ? answers[currentQuestion.id] : undefined;

  return (
    <div className="min-h-dvh flex flex-col bg-bg-alt">
      <GuideAppHeader
        step={step}
        title={config.headerTitle}
        currentQuestion={step === 'question' ? currentQuestionIndex + 1 : undefined}
        totalQuestions={step === 'question' ? config.questions.length : undefined}
        onBack={step === 'question' ? handleHeaderBack : undefined}
      />

      {step === 'start' && (
        <div className="flex-1 flex flex-col">
          <GuideStart config={config} onStart={handleStart} />
        </div>
      )}

      {step === 'question' && (
        <>
          <div className="flex-1 max-w-[720px] mx-auto w-full px-5 md:px-6 pt-20 md:pt-8 pb-32 md:pb-14">
            <div className="mb-3">
              <GuideProgress
                current={currentQuestionIndex + 1}
                total={config.questions.length}
                category={currentQuestion.category}
              />
            </div>

            <GuideQuestion
              question={currentQuestion}
              questionIndex={currentQuestionIndex}
              totalQuestions={config.questions.length}
              onAnswer={handleAnswer}
              savedSelectedIds={savedSelectedIds}
            />

            <div className="hidden md:block mt-6">
              <button
                onClick={handleNext}
                disabled={!hasSelection}
                className={`w-full h-[44px] rounded-full text-[14px] font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 ${
                  hasSelection
                    ? 'bg-primary-normal text-static-white hover:bg-primary-strong'
                    : 'bg-fill-strong text-label-disabled cursor-not-allowed'
                }`}
              >
                {isLastQuestion ? '결과 보기' : '다음 질문'}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>

          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-5 py-3 pb-5 bg-bg-alt border-t border-line-divider">
            <button
              onClick={handleNext}
              disabled={!hasSelection}
              className={`w-full h-[44px] rounded-full text-[14px] font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1.5 ${
                hasSelection
                  ? 'bg-primary-normal text-static-white hover:bg-primary-strong'
                  : 'bg-fill-strong text-label-disabled cursor-not-allowed'
              }`}
            >
              {isLastQuestion ? '결과 보기' : '다음 질문'}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </>
      )}

      {step === 'analyzing' && (
        <div className="flex-1 flex items-center justify-center px-5 py-12">
          <div className="w-full max-w-[420px] mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-fill-normal flex items-center justify-center mx-auto mb-6">
              <i className="ri-loader-4-line text-primary-normal text-2xl animate-spin"></i>
            </div>

            <h2 className="font-serif text-[24px] md:text-[28px] text-label-normal mb-3">
              답변을 정리하고 있어요
            </h2>
            <p className="text-[15px] text-label-alt mb-8 whitespace-pre-line leading-relaxed">
              선택하신 기준을 바탕으로{' '}
              가장 가까운 방향을 찾고 있어요.
            </p>

            <div className="space-y-2.5 text-left max-w-[280px] mx-auto">
              {ANALYZING_CHECK_ITEMS.map((item, i) => (
                <div
                  key={item}
                  className="flex items-center gap-3 text-label1 transition-all duration-300"
                  style={{
                    opacity: i < analyzingChecks ? 1 : 0.4,
                  }}
                >
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      i < analyzingChecks ? 'bg-primary-normal' : 'bg-fill-strong'
                    }`}
                  >
                    <i className="ri-check-line text-static-white text-xs"></i>
                  </div>
                  <span className={i < analyzingChecks ? 'text-label-neutral' : 'text-label-assistive'}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
